// Tiny helper pair that lets us round-trip a non-default return URL
// across the SSO / magic-link redirects that send the user off our
// origin and bounce them back through /auth/callback.
//
// Default behaviour for those callbacks is to drop the user on "/"
// after auth. That's fine for normal sign-in, but breaks the
// /oauth/desktop?session=… handoff used by the Cadence Mac app and the
// /oauth/authorize?... handoff used by MCP connectors — both pages need
// the original query string to complete their flow. We stash the URL
// in sessionStorage before any cross-origin redirect, and `consume()`
// pulls it back out on the return leg.

const KEY = 'cadence:auth-handoff-return-url';

/// Path prefixes that warrant preservation. The default "/" reload is
/// the right behaviour for any non-handoff path, so we only stash when
/// the user is mid-flow on one of these surfaces.
const HANDOFF_PREFIXES = ['/oauth/desktop', '/oauth/authorize'];

/// If the current URL is a known auth-handoff page, store
/// `pathname + search + hash` so we can return to it after the SSO /
/// magic-link round trip. No-op otherwise.
export function stashReturnUrlIfAuthHandoff(): void {
    stashSetupReturn();
    const path = window.location.pathname;
    if (!HANDOFF_PREFIXES.some(p => path.startsWith(p))) {
        console.info('[cadence] auth handoff stash skipped — path was', path);
        return;
    }
    const fullUrl = window.location.pathname + window.location.search + window.location.hash;
    try {
        sessionStorage.setItem(KEY, fullUrl);
        console.info('[cadence] auth handoff stash set →', fullUrl);
    } catch (e) {
        // sessionStorage can throw in private-browsing mode. The
        // consequence is that the user lands on "/" after SSO — better
        // than crashing.
        console.warn('[cadence] auth handoff stash failed', e);
    }
}

/// If the current URL is a known auth-handoff page, return its
/// `pathname + search + hash` so it can be threaded through a server-side
/// OAuth round-trip (as a `returnUrl` query param on /auth/login/*). This is
/// the server-side belt to sessionStorage's braces: the in-app browser used by
/// the native apps can drop sessionStorage across the provider hop, so the
/// social-login links also pass the handoff URL to the backend, which echoes it
/// back on the success redirect. Returns null on any non-handoff page.
export function currentAuthHandoffReturnUrl(): string | null {
    const path = window.location.pathname;
    if (!HANDOFF_PREFIXES.some(p => path.startsWith(p))) return null;
    return window.location.pathname + window.location.search + window.location.hash;
}

/// A return address only ever goes back into this site: it must resolve to
/// this origin. "Starts with / but not //" let "/\\evil.com" through, which
/// browsers read as "//evil.com", so a crafted /auth/callback?error=…&
/// returnUrl= link bounced people to another site.
export function sameSitePath(raw: string | null | undefined): string | null {
    if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
    if (Array.from(raw).some((ch) => ch.charCodeAt(0) < 0x20)) return null;
    try {
        const url = new URL(raw, window.location.origin);
        return url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;
    } catch {
        return null;
    }
}

/// Pull and clear any stashed return URL. Returns null when nothing
/// was stashed (the normal case for a plain sign-in).
export function consumeReturnUrl(): string | null {
    try {
        const value = sessionStorage.getItem(KEY);
        if (value) {
            sessionStorage.removeItem(KEY);
            console.info('[cadence] auth handoff stash consumed →', value);
        } else {
            console.info('[cadence] auth handoff stash was empty');
        }
        return sameSitePath(value);
    } catch (e) {
        console.warn('[cadence] auth handoff stash read failed', e);
        return null;
    }
}

// ── Guided setup links ──────────────────────────────────────────────────────
// "Email me the link" sends `/?setup=next` (or `/settings`, or the old `/setup`); the person opens it
// on a computer that may not be signed in. Password and passkey sign-in keep
// the address; SSO, Apple and Microsoft leave the site, so the path is kept in
// sessionStorage and taken back on the return leg. Only these exact shapes are
// kept, so this can never send anyone anywhere else.

const SETUP_KEY = 'cadence:setup-return-path';
const SETUP_PATH = /^\/(\?setup=[a-z]+|setup(\/health)?|settings(\/[a-z-]+)?)$/;

/// The current address when it's a guided-setup link, else null.
export function setupReturnPath(): string | null {
    const { pathname, search } = window.location;
    const step = new URLSearchParams(search).get('setup');
    const path = pathname === '/' && step ? `/?setup=${step}` : pathname;
    return SETUP_PATH.test(path) ? path : null;
}

function stashSetupReturn(): void {
    const path = setupReturnPath();
    if (!path) return;
    try { sessionStorage.setItem(SETUP_KEY, path); } catch { /* private browsing: lands on "/" */ }
}

/// Pull and clear a stashed guided-setup path (null when none, or not one of ours).
export function consumeSetupReturn(): string | null {
    try {
        const value = sessionStorage.getItem(SETUP_KEY);
        sessionStorage.removeItem(SETUP_KEY);
        return value && SETUP_PATH.test(value) ? value : null;
    } catch {
        return null;
    }
}
